import { useMemo } from 'react';
import { Card, CardBody } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Boxes } from 'lucide-react';
import { useRackItems, type Item, type ItemType } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { itemStatusCell } from '../items/items-status';
import { itemDetailPath } from '../items/list-configs/item-column-definitions';

const muted = { color: 'var(--vn-fg-muted)' } as const;

/** "U{n}" for a positive slot, else an em dash. */
function positionLabel(u?: number): string {
  return u && u > 0 ? `U${u}` : '—';
}

/** Columns for a rack's mounted items: name link · service tag/ref · status · U · condition. */
const itemColumns: ColumnDef<Item, unknown>[] = [
  {
    id: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <TableLink to={itemDetailPath(row.original)} title={row.original.name} truncate>
        {row.original.name || row.original.id}
      </TableLink>
    ),
  },
  {
    id: 'ref',
    header: 'Service tag / Ref',
    cell: ({ row }) => {
      const ref = row.original.service_tag ?? row.original.ref;
      return ref ? <span title={ref}>{ref}</span> : <span style={muted}>—</span>;
    },
  },
  { id: 'status', header: 'Status', cell: ({ row }) => itemStatusCell(row.original.status) },
  {
    id: 'position',
    header: 'Position',
    cell: ({ row }) => <span>{positionLabel(row.original.rack_position_u)}</span>,
  },
  {
    id: 'condition',
    header: 'Condition',
    cell: ({ row }) =>
      row.original.condition ? (
        <span style={{ textTransform: 'capitalize' }}>{row.original.condition}</span>
      ) : (
        <span style={muted}>—</span>
      ),
  },
];

export interface RackItemsTabProps {
  rackId: string;
  /** Which slice of the rack's items to show. */
  itemType: ItemType;
}

/**
 * Equipment-in-Rack / Components-in-Rack tab. Loads every item mounted in the
 * rack via `useRackItems` and filters client-side by `item_type`, rendering the
 * matching slice as a DataTable (name links out to the equipment/component
 * detail route). Loading → skeleton, error → retry, empty → EmptyState.
 */
export function RackItemsTab({ rackId, itemType }: RackItemsTabProps) {
  const { data, isLoading, isError, refetch } = useRackItems(rackId);

  const rows = useMemo(
    () => (data?.results ?? []).filter((i) => i.item_type === itemType),
    [data, itemType],
  );

  if (isError) {
    return <TabError title="Failed to load rack items" onRetry={() => refetch()} />;
  }
  if (isLoading) {
    return <TabLoading rows={6} />;
  }

  const noun = itemType === 'equipment' ? 'equipment' : 'components';

  return (
    <Card flat>
      <CardBody>
        <DataTable
          columns={itemColumns}
          data={rows}
          getRowId={(r) => r.id}
          emptyState={
            <EmptyState
              icon={<Boxes size={24} />}
              title={`No ${noun} in this rack`}
              description={`This rack has no mounted ${noun}.`}
            />
          }
        />
      </CardBody>
    </Card>
  );
}
